import { useDeferredValue, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Spinner } from "@heroui/react";
import R1FilterBar from "../../components/R1FilterBar";
import { r1Api } from "../../services/api";
import { toast } from "sonner";
import { useReconciliationViewModel } from "../hooks/useReconciliationViewModel";
import { ReconciliationSummaryCards } from "./ReconciliationSummaryCards";
import { TrackerSection } from "./TrackerSection";
import { FundReconciliationSection } from "./FundReconciliationSection";
import { CompanyReconciliationSection } from "./CompanyReconciliationSection";
import { AuditTrailSection } from "./AuditTrailSection";
import { ReconciliationEditSheet } from "./ReconciliationEditSheet";
import { ReconciliationCell } from "./ReconciliationCell";
import {
  buildPreviousIndicator,
  getCanonicalFieldKey,
  makeOverrideKey,
  getIssueTypeLabel,
  getPriority,
} from "../domain/status";
import {
  FUND_COLS,
  ISSUER_COLS,
  POSITION_COLS,
  fmtNum,
  parseArrayValue,
} from "../domain/formatters";
import type { EditTarget } from "../domain/types";
import { ValidationPanel } from "../../validation/ui/ValidationPanel";

function getApiErrorMessage(error: unknown): string {
  const anyError = error as {
    response?: { data?: { detail?: unknown; message?: unknown } };
  };
  const data = anyError.response?.data;
  const detail = data?.detail;

  if (typeof detail === "string" && detail.trim()) return detail;

  if (detail && typeof detail === "object") {
    const d = detail as { code?: unknown; message?: unknown; field?: unknown };
    const message =
      typeof d.message === "string" && d.message.trim()
        ? d.message
        : "Request failed";
    const code = typeof d.code === "string" && d.code.trim() ? d.code : null;
    const field =
      typeof d.field === "string" && d.field.trim() ? ` (${d.field})` : "";
    return code ? `${message}${field} [${code}]` : `${message}${field}`;
  }

  if (typeof data?.message === "string" && data.message.trim()) {
    return data.message;
  }

  return "Request failed";
}

const COMPANY_EXCLUDED_CALCULATED_KEYS = new Set([
  "r1_unique_identifier",
  "pc_new_in_total_portfolio",
  "pc_new_in_fund_portfolio",
  "date_pc_new_in_r1_fund_portfolio",
]);

const COMPANY_ISSUER_COLS = ISSUER_COLS.filter(
  (col) => !COMPANY_EXCLUDED_CALCULATED_KEYS.has(col.key),
);
const COMPANY_POSITION_COLS = POSITION_COLS.filter(
  (col) => !COMPANY_EXCLUDED_CALCULATED_KEYS.has(col.key),
);
const COMPANY_ISSUER_COLS_ORDERED = [
  ...COMPANY_ISSUER_COLS.filter((col) => col.key !== "pc_description"),
  ...COMPANY_ISSUER_COLS.filter((col) => col.key === "pc_description"),
];
const COMPANY_SHORT_DESCRIPTION_COL = COMPANY_ISSUER_COLS_ORDERED.find(
  (col) => col.key === "pc_description",
);
const COMPANY_ISSUER_COLS_NO_SHORT_DESCRIPTION =
  COMPANY_ISSUER_COLS_ORDERED.filter((col) => col.key !== "pc_description");

export function ReconciliationPage() {
  const queryClient = useQueryClient();
  const {
    selectedFundIds,
    selectedPeriod,
    periodsPerFund,
    selectedFunds,
    selectedFundsById,
    issuerMap,
    auditTrail,
    overrides,
    previousAuditValuesByKey,
    controls,
    controlCellKeyByTuple,
    previousControlsByKey,
    controlsHistoryByIdentity,
    controlsByFundIssuer,
    controlCompaniesByFund,
    companies,
    companiesTotal,
    totalPositionsCount,
    currentPage,
    setCurrentPage,
    pageCount,
    companyDeltaSummary,
    colorSummary,
    selectedPeriodLabel,
    isEffectivelyEmpty,
    isLoading,
    refetchAudit,
    canEdit,
    editLockTooltip,
  } = useReconciliationViewModel();

  // Defer the heavy controls re-render so the table appears immediately
  // and cell dots/colours update in the background without blocking the UI.
  const deferredControls = useDeferredValue(controls);
  const deferredControlCellKeyByTuple = useDeferredValue(controlCellKeyByTuple);
  const deferredPreviousControlsByKey = useDeferredValue(previousControlsByKey);

  const [editing, setEditing] = useState<EditTarget | null>(null);
  const [editValue, setEditValue] = useState("");
  const [editComment, setEditComment] = useState("");
  const [isFalsePositiveMode, setIsFalsePositiveMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const patchCellInCache = (
    cellKey: string | undefined,
    patch: Partial<import("../data/dto").ReconciliationCellDto>,
  ): boolean => {
    if (!cellKey) return false;
    let foundAny = false;
    queryClient.setQueriesData(
      { queryKey: ["r1", "positions-issuers-reconciliation-cells"] },
      (old: unknown) => {
        if (!old || typeof old !== "object" || !("data" in old)) return old;
        const typed = old as { data?: import("../data/dto").ReconciliationCellDto[] };
        if (!Array.isArray(typed.data)) return old;
        let foundInThisQuery = false;
        const data = typed.data.map((cell) => {
          if (cell.cell_key !== cellKey) return cell;
          foundInThisQuery = true;
          foundAny = true;
          return { ...cell, ...patch };
        });
        return foundInThisQuery ? { ...typed, data } : old;
      },
    );
    return foundAny;
  };

  const openEditor = (target: EditTarget) => {
    const key = makeOverrideKey(
      target.entityType,
      target.entityId,
      target.fieldKey,
    );
    const existing = overrides[key];
    const rawValue = (() => {
      if (target.rawValue === null || target.rawValue === undefined) return "";
      if (target.isArray) {
        const items = parseArrayValue(target.rawValue);
        return items ? JSON.stringify(items) : String(target.rawValue);
      }
      return String(target.rawValue);
    })();
    setEditing(target);
    setEditValue(existing?.value ?? rawValue);
    setEditComment(existing?.comment ?? "");
    setIsFalsePositiveMode(false);
  };

  const saveEdit = async () => {
    if (!editing) return;
    const rawValue = editValue.trim();
    // For array fields, normalize to strict JSON array before sending.
    // An empty editValue sends "[]" which clears the field.
    const value = editing.isArray
      ? (() => {
          if (!rawValue) return "[]";
          const items = parseArrayValue(rawValue);
          return items ? JSON.stringify(items) : rawValue;
        })()
      : rawValue;
    const comment = editComment.trim();
    if (isFalsePositiveMode) {
      if (!comment) {
        toast.error("Comment is required for false positive", { position: "bottom-left" });
        return;
      }
      try {
        setIsSaving(true);
        await r1Api.createPositionsIssuersReconciliationFalsePositive({
          reconciliation_control_id: editing.control?.id ?? undefined,
          comment,
          fund_id: editing.control?.fundId ?? undefined,
          issuer_id: editing.control?.issuerId ?? undefined,
          position_id: editing.control?.positionId ?? undefined,
          section_name: editing.control?.sectionName ?? undefined,
          field_name: editing.control?.fieldName ?? editing.fieldKey,
          reporting_date: editing.control?.reportingDate ?? undefined,
          trr_value: editing.trrValue ?? undefined,
          pdf_value: editing.pdfValue ?? undefined,
          golden_value: editing.goldenValue ?? undefined,
          business_key: editing.control?.businessKey ?? undefined,
        });
        patchCellInCache(editing.control?.cellKey, {
          color_code: "green_dark",
          display_color: "green_dark",
          reconciliation_status: "false_positive",
          has_raw_mismatch: false,
          has_pdf_mismatch: false,
          has_golden_mismatch: false,
        });
        setEditing(null);
        setEditValue("");
        setEditComment("");
        setIsFalsePositiveMode(false);
        toast.success("False positive comment saved", { position: "bottom-left" });
        void refetchAudit();
        void queryClient.invalidateQueries({
          queryKey: ["r1", "positions-issuers-reconciliation-controls-history"],
          refetchType: "none",
        });
        void queryClient.invalidateQueries({
          queryKey: ["r1", "reconciliation-companies"],
          refetchType: "none",
        });
      } catch (error) {
        console.error("Could not save false positive comment", error);
        toast.error(getApiErrorMessage(error), { position: "bottom-left" });
      } finally {
        setIsSaving(false);
      }
      return;
    }
    try {
      setIsSaving(true);
      const overrideResult = await r1Api.savePositionsIssuersReconciliationOverride({
        entity_type: editing.entityType,
        entity_id: editing.entityId,
        field_key: editing.fieldKey,
        value,
        comment,
        reconciliation_control_id: editing.control?.id ?? undefined,
        fund_id: editing.control?.fundId ?? editing.fundId,
        reporting_date: editing.control?.reportingDate ?? undefined,
      });
      const patched = patchCellInCache(editing.control?.cellKey, {
        color_code: "violet",
        display_color: "violet",
        has_override: true,
        golden_value: value,
        display_value: value,
      });
      if (!patched) {
        if (editing.entityType === "fund") {
          // NAV cells are synthetic (built from capital-account data, not stored
          // in reconciliation-cells). Refetch CA so the ViewModel rebuilds them.
          void queryClient.invalidateQueries({ queryKey: ["r1", "capital-account"] });
        } else {
          // Cell not found in cache — happens for removed companies (controls from
          // a previous period) or first-time overrides. Refresh both cells (to pick
          // up the newly created control) and issuers (so the value updates via
          // issuerMap even if the control period doesn't match the current period).
          void queryClient.invalidateQueries({
            queryKey: ["r1", "positions-issuers-reconciliation-cells"],
          });
          void queryClient.invalidateQueries({
            queryKey: ["r1", "issuers-by-ids"],
          });
        }
      }
      setEditing(null);
      setEditValue("");
      setEditComment("");
      setIsFalsePositiveMode(false);
      if (overrideResult.warning) {
        const { validated_funds, non_validated_funds } = overrideResult.warning;
        const currentFundIsValidated = validated_funds.some(
          (f) => f.fund_id === (editing.control?.fundId ?? editing.fundId),
        );
        if (currentFundIsValidated) {
          const names = non_validated_funds.map((f) => f.fund_name).join(", ");
          toast.warning(
            `Override saved — this issuer is also in non-validated fund(s): ${names}`,
            { position: "bottom-left" },
          );
        } else {
          const names = validated_funds.map((f) => f.fund_name).join(", ");
          toast.warning(
            `Override saved — this issuer also appears in validated fund(s): ${names}`,
            { position: "bottom-left" },
          );
        }
      } else {
        toast.success("Override saved", { position: "bottom-left" });
      }
      void refetchAudit();
      void queryClient.invalidateQueries({
        queryKey: ["r1", "positions-issuers-reconciliation-controls-history"],
        refetchType: "none",
      });
      void queryClient.invalidateQueries({
        queryKey: ["r1", "reconciliation-companies"],
        refetchType: "none",
      });
    } catch (error) {
      console.error("Could not save reconciliation override", error);
      toast.error(getApiErrorMessage(error), { position: "bottom-left" });
    } finally {
      setIsSaving(false);
    }
  };

  const editingPreviousValue = useMemo(() => {
    if (!editing) return undefined;
    const fundId = editing.control?.fundId;
    const issuerId = editing.control?.issuerId;
    const positionId = editing.control?.positionId ?? editing.entityId;
    const field = getCanonicalFieldKey(editing.fieldKey);
    if (!fundId) return undefined;
    const identityKey =
      editing.entityType === "fund"
        ? `${fundId}::fund::${field}`
        : editing.entityType === "position"
          ? `${fundId}::position::${positionId}::${field}`
          : `${fundId}::issuer::${issuerId ?? editing.entityId}::${field}`;
    const series = controlsHistoryByIdentity[identityKey] ?? [];
    const currentDate = editing.control?.reportingDate ?? null;
    const previous = series.find((v) =>
      currentDate === null || currentDate === undefined
        ? true
        : (v.reportingDate ?? 0) < currentDate,
    );
    if (!previous) return undefined;
    return {
      golden: previous.goldenValue,
      trr: previous.trrValue,
      pdf: previous.pdfValue,
    };
  }, [editing, controlsHistoryByIdentity]);
  const goldenIndicator = buildPreviousIndicator(
    editing?.goldenValue ?? editing?.formattedValue,
    editingPreviousValue?.golden,
  );
  const trrIndicator = buildPreviousIndicator(
    editing?.trrValue,
    editingPreviousValue?.trr,
  );
  const pdfIndicator = buildPreviousIndicator(
    editing?.pdfValue,
    editingPreviousValue?.pdf,
  );

  const trrRawValue = editing?.trrValue;
  const pdfRawValue = editing?.pdfValue;

  const activeFundId = selectedFundIds[0];

  if (selectedFundIds.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <R1FilterBar singleSelect />
        <div className="flex h-48 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-white text-sm text-gray-400">
          Select a fund above
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <R1FilterBar singleSelect />
        <div className="flex h-48 items-center justify-center">
          <Spinner label="Loading…" color="primary" />
        </div>
      </div>
    );
  }

  const activeFundName =
    selectedFundsById.get(activeFundId ?? 0)?.fund_name ?? null;

  return (
    <div className="flex flex-col gap-6">
      <R1FilterBar singleSelect />

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        {/* Page header */}
        <div className="flex flex-wrap items-start justify-between gap-6 px-8 py-8 pb-6">
          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-widest text-slate-400">
              R1 · Data Reconciliation
            </p>
            <h1 className="text-5xl leading-none font-serif text-slate-900">
              {activeFundName ?? "Reconciliation"}
            </h1>
            <p className="mt-2 text-sm text-slate-400">
              Data Production Environment — XLSX &amp; PDF files
            </p>
          </div>
          <span
            className={
              isEffectivelyEmpty
                ? "rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700 ring-1 ring-amber-200"
                : "rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 ring-1 ring-emerald-200"
            }
          >
            {isEffectivelyEmpty ? "Data missing" : "Data loaded"}
          </span>
        </div>

        {/* Validation workflow */}
        {activeFundId && selectedPeriod && (
          <div className="border-t border-slate-100 px-8 py-6">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-2xl font-serif text-slate-900">
                Validation workflow
              </h2>
              <span className="text-xs text-slate-400">{selectedPeriod}</span>
            </div>
            <ValidationPanel fundId={activeFundId} quarter={selectedPeriod} />
          </div>
        )}

        {/* Tracker */}
        {activeFundId && selectedPeriod && (
          <div className="border-t border-slate-100 px-8 py-6">
            <div className="mb-5 flex items-center gap-3">
              <h2 className="text-2xl font-serif text-slate-900">Tracker</h2>
            </div>
            <TrackerSection fundId={activeFundId} quarter={selectedPeriod} />
          </div>
        )}

        {/* Content */}
        <div className="border-t border-slate-100 px-8 pb-10 pt-6">
          {isEffectivelyEmpty && (
            <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <p className="font-semibold">
                No imported reconciliation data for this fund.
              </p>
              <p className="mt-1">
                Period{" "}
                <span className="font-medium">{selectedPeriodLabel}</span>.
              </p>
              <p className="mt-1 text-amber-800">
                Upload XLS/PDF inputs for this fund, then refresh to see
                positions, reconciliation controls, field dots, and conflicts.
              </p>
            </div>
          )}

          <ReconciliationSummaryCards
            colorSummary={colorSummary}
            companyDeltaSummary={companyDeltaSummary}
          />

          <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-emerald-600" />
              Match
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-emerald-300" />
              Single source
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-orange-300" />
              All empty
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-red-500" />
              Conflict
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-violet-500" />
              Override
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-slate-400" />
              Neutral
            </span>
          </div>

          <FundReconciliationSection
            selectedFunds={selectedFunds.filter(
              (f) => Number(f.id ?? f.fund_id) === activeFundId,
            )}
            fundCols={FUND_COLS}
            periodsPerFund={periodsPerFund}
            selectedPeriod={selectedPeriod}
            renderCell={(args) => (
              <ReconciliationCell
                {...args}
                controls={deferredControls}
                controlCellKeyByTuple={deferredControlCellKeyByTuple}
                previousControls={deferredPreviousControlsByKey}
                previousAuditValues={previousAuditValuesByKey}
                onEdit={openEditor}
                canEdit={canEdit}
                editLockTooltip={editLockTooltip}
              />
            )}
          />

          <CompanyReconciliationSection
            activeFundId={activeFundId ?? 0}
            selectedFundsById={selectedFundsById}
            companies={companies}
            companiesTotal={companiesTotal}
            totalPositionsCount={totalPositionsCount}
            currentPage={currentPage}
            pageCount={pageCount}
            onPageChange={setCurrentPage}
            issuerMap={issuerMap}
            controlCompaniesByFund={controlCompaniesByFund}
            controlsByFundIssuer={controlsByFundIssuer}
            companyIssuerColsNoShortDescription={
              COMPANY_ISSUER_COLS_NO_SHORT_DESCRIPTION
            }
            companyPositionCols={COMPANY_POSITION_COLS}
            companyShortDescriptionCol={COMPANY_SHORT_DESCRIPTION_COL}
            renderCell={({ hideDot, ...args }) => (
              <ReconciliationCell
                {...args}
                controls={deferredControls}
                controlCellKeyByTuple={deferredControlCellKeyByTuple}
                previousControls={deferredPreviousControlsByKey}
                previousAuditValues={previousAuditValuesByKey}
                hideDot={hideDot}
                onEdit={openEditor}
                canEdit={canEdit}
                editLockTooltip={editLockTooltip}
              />
            )}
            getIssueTypeLabel={getIssueTypeLabel}
            getPriority={getPriority}
            fmtNum={fmtNum}
            selectedPeriodLabel={selectedPeriodLabel}
          />

          <div className="mt-10">
            <div className="mb-5 flex items-center gap-3">
              <h2 className="text-2xl font-serif text-slate-900">
                Audit trail
              </h2>
            </div>
            <AuditTrailSection auditTrail={auditTrail} />
          </div>
        </div>
      </div>

      <ReconciliationEditSheet
        editing={editing}
        isSaving={isSaving}
        editValue={editValue}
        editComment={editComment}
        isFalsePositiveMode={isFalsePositiveMode}
        trrRawValue={trrRawValue}
        pdfRawValue={pdfRawValue}
        goldenIndicator={goldenIndicator}
        trrIndicator={trrIndicator}
        pdfIndicator={pdfIndicator}
        setEditing={setEditing}
        setEditValue={setEditValue}
        setEditComment={setEditComment}
        setIsFalsePositiveMode={setIsFalsePositiveMode}
        onSave={saveEdit}
      />
    </div>
  );
}
